import { shortUrlSample } from "constant/messageConstants";

/**
 * @param {object|null|undefined} user
 * @returns {{ url: string|null, status: string, enabled: boolean, rejectionReason?: string|null }}
 */
export function getShorturlDomain(user) {
  const nested = user?.shorturlDomain;
  if (!nested || typeof nested !== "object") {
    return { url: null, status: "none", enabled: true, rejectionReason: null };
  }
  return {
    url: nested.url || null,
    status: nested.status || "none",
    enabled: nested.enabled !== false,
    rejectionReason: nested.rejectionReason || null,
    submittedAt: nested.submittedAt || null,
    reviewedAt: nested.reviewedAt || null
  };
}

/**
 * Domain is approved by SuperAdmin (toggle may still be off).
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export function hasApprovedBrandedDomain(user) {
  const { url, status } = getShorturlDomain(user);
  return Boolean(status === "approved" && url && String(url).trim());
}

/**
 * Approved and toggled on for SMS short links.
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export function isBrandedShortDomainActive(user) {
  const { enabled } = getShorturlDomain(user);
  return hasApprovedBrandedDomain(user) && enabled;
}

/**
 * Effective branded host when approved+enabled; otherwise null (use env default).
 * @param {object|null|undefined} user
 * @returns {string|null}
 */
export function resolveShortDomain(user) {
  if (!isBrandedShortDomainActive(user)) return null;
  return String(getShorturlDomain(user).url)
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//i, "")
    .replace(/\/+$/, "")
    .split("/")[0];
}

/**
 * Placeholder inserted into compose.
 * Active branded domain → `rahulll.com/------`
 * Default / toggled off → env token (`REACT_SHORT_URL_SAMPLE`).
 * @param {object|null|undefined} user
 * @returns {string}
 */
export function getShortUrlInsertSample(user) {
  const domain = resolveShortDomain(user);
  if (domain) return `${domain}/------`;
  return shortUrlSample;
}

/**
 * Length/preview sample — matches what is inserted into compose.
 * @param {object|null|undefined} user
 * @returns {string}
 */
export function getShortUrlLengthSample(user) {
  return getShortUrlInsertSample(user);
}

/**
 * Markers that may appear in compose (insert sample + env token for mixed drafts).
 * @param {object|null|undefined} user
 * @returns {string[]}
 */
export function getShortUrlMarkerSamples(user) {
  const samples = new Set([getShortUrlInsertSample(user)]);
  if (shortUrlSample) samples.add(shortUrlSample);
  return [...samples].filter(Boolean);
}

/**
 * @param {string} message
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export function messageHasShortUrlMarker(message, user) {
  if (!message) return false;
  return getShortUrlMarkerSamples(user).some(sample => message.includes(sample));
}

/**
 * Remove any short-URL markers from the message body.
 * @param {string} message
 * @param {object|null|undefined} user
 * @returns {string}
 */
export function clearShortUrlMarkers(message, user) {
  let out = message || "";
  for (const sample of getShortUrlMarkerSamples(user)) {
    out = out.split(sample).join("");
  }
  return out;
}

/**
 * For length/preview: only swap env token → branded sample when active.
 * @param {string} message
 * @param {object|null|undefined} user
 * @returns {string}
 */
export function replaceShortUrlPlaceholderForLength(message, user) {
  if (!message) return "";
  if (!isBrandedShortDomainActive(user)) return message;
  const insert = getShortUrlInsertSample(user);
  let out = message;
  if (shortUrlSample && insert && out.includes(shortUrlSample)) {
    out = out.split(shortUrlSample).join(insert);
  }
  return out;
}
